import { ThemeProvider, TooltipProvider } from '@quadminds/mindkit';
import {
    MindyDocRoles,
    MindyKnowledgePermissions,
    MindyStandardRoles,
    MindyUsersPermissions,
} from '@quadminds/mindy-shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import RequirePermission from './require-permission';
import AuthWrapper from '../components/common/Auth/Auth';
import GeoLimitExceeded from '../components/common/Errors/GeoLimitExceeded';
import LimitExceeded from '../components/common/Errors/LimitExceeded';
import PageNotFound from '../components/common/Errors/PageNotFound';
import UserSuspended from '../components/common/Errors/ResrtrictedAccess';
import UserNotFound from '../components/common/Errors/UserNotFound';
import ErrorBoundary from '../components/default.error.boundry';
import { Layout } from '../components/layout/Layout';
import OutScopeError from '../components/layout/OutScopeError/OutScopeError';
import { Onboarding } from '../components/Onboarding/Onboarding';
import AuthCallback from '../components/pages/AuthCallback/AuthCallback.component';
import { Chat } from '../components/pages/Chat/Chat/Chat';
import { Embedded } from '../components/pages/Embedded/Embedded/Embedded';
import { Knowledge } from '../components/pages/Knowledge/Knowledge/Knowledge';
import QuestionPage from '../components/pages/Questions/QuestionPage';
import Connections from '../components/pages/Settings/Connections/Connections';
import SettingsLayout from '../components/pages/Settings/Layout/Layout';
import { Roles } from '../components/pages/Settings/Roles/Roles';
import { Users } from '../components/pages/Settings/Users/Users';

import { ByPassProvider } from '../providers/ByPass/ByPass.provider';
import { ChatProvider } from '../providers/Chat/Chat.provider';
import { FirebaseProvider } from '../providers/Firebase/Firebase.provider';
import { IntegrationProvider } from '../providers/Integration/Integration.provider';
import { KnowledgeProvider } from '../providers/Knowledge/Knowledge.provider';

const MINDY_DOMAINS = ['mindy-test.lat', 'mindy.lat'];
const APP_BASE_PATH = '/apps/mindy-app';

const canAccessSettings = (userInfo: { permissions?: string[]; roles?: string[] }): boolean =>
    !!userInfo.roles?.includes(MindyStandardRoles.ADMIN) || !!userInfo.roles?.includes(MindyDocRoles.ADMIN_DOC);

const canAccessKnowledge = (userInfo: { permissions?: string[]; roles?: string[] }): boolean =>
    !!userInfo.permissions?.includes(MindyKnowledgePermissions.ReadKnowledge) ||
    !!userInfo.roles?.includes(MindyDocRoles.ADMIN_DOC) ||
    !!userInfo.roles?.includes(MindyDocRoles.MANAGER_DOC);

interface AppProps {
    readonly queryClient: QueryClient;
}
export function App({ queryClient }: AppProps) {
    if (
        MINDY_DOMAINS.includes(globalThis.location.hostname) &&
        !globalThis.location.pathname.startsWith(APP_BASE_PATH)
    ) {
        globalThis.location.replace(`${APP_BASE_PATH}/chat${globalThis.location.search}`);
        return null;
    }

    return (
        <ErrorBoundary fallback={<p className="text-red-500">Ocurrió un error inesperado</p>}>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter basename="apps/mindy-app">
                    <AuthWrapper>
                        <ThemeProvider defaultTheme="dark" storageKey="my-app-theme">
                            <FirebaseProvider>
                                <IntegrationProvider>
                                    <ByPassProvider>
                                        <Onboarding />
                                        <Routes>
                                            {/* Shared Layout — persists across navigation, no flash on route change */}
                                            <Route element={<Layout />}>
                                                <Route path="/chat">
                                                    <Route path=":id?" element={<Chat />} />
                                                </Route>
                                                <Route path="/suggested-questions">
                                                    <Route path="" element={<QuestionPage />} />
                                                </Route>
                                                <Route
                                                    path="/knowledge/*"
                                                    element={
                                                        <RequirePermission customEval={canAccessKnowledge}>
                                                            <KnowledgeProvider>
                                                                <Knowledge />
                                                            </KnowledgeProvider>
                                                        </RequirePermission>
                                                    }
                                                />
                                                <Route path="/settings">
                                                    <Route
                                                        path="*"
                                                        element={
                                                            <RequirePermission customEval={canAccessSettings}>
                                                                <SettingsLayout />
                                                            </RequirePermission>
                                                        }
                                                    >
                                                        <Route
                                                            path="users"
                                                            element={
                                                                <RequirePermission customEval={canAccessSettings}>
                                                                    <Users />
                                                                </RequirePermission>
                                                            }
                                                        />
                                                        <Route
                                                            path="roles"
                                                            element={
                                                                <RequirePermission
                                                                    permisions={[MindyUsersPermissions.ReadUser]}
                                                                >
                                                                    <Roles />
                                                                </RequirePermission>
                                                            }
                                                        />
                                                        <Route path="connections" element={<Connections />} />
                                                        <Route
                                                            path="*"
                                                            element={<Navigate to="/settings/users" replace />}
                                                        />
                                                    </Route>
                                                </Route>
                                                <Route path="/error">
                                                    <Route path="geo-limit" element={<GeoLimitExceeded />} />
                                                    <Route path="limit-exceeded" element={<LimitExceeded />} />
                                                </Route>
                                                <Route path="/page-not-found" element={<PageNotFound />} />
                                            </Route>

                                            {/* Routes without shared Layout */}
                                            <Route
                                                path="/embedded"
                                                element={
                                                    <TooltipProvider>
                                                        <ChatProvider>
                                                            <Embedded />
                                                        </ChatProvider>
                                                    </TooltipProvider>
                                                }
                                            />
                                            <Route path="/auth" element={<AuthCallback />} />
                                            <Route path="/error-out-scope" element={<OutScopeError />}>
                                                <Route path="restricted-access" element={<UserSuspended />} />
                                                <Route path="user-not-found" element={<UserNotFound />} />
                                            </Route>
                                            <Route path="/" element={<Navigate to="/chat" replace />} />
                                            <Route path="*" element={<Navigate to="/page-not-found" replace />} />
                                        </Routes>
                                    </ByPassProvider>
                                </IntegrationProvider>
                            </FirebaseProvider>
                        </ThemeProvider>
                    </AuthWrapper>
                </BrowserRouter>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}

export default App;
